import api from './api';
import type { TenantStore } from '@/mocks/stores';
import type { StoreValuationMethod } from '@/constants/inventory';
import { StoreState } from '@tcr-erp/shared';

export type { TenantStore };

export interface StoreSettings {
  name: string;
  code: string;
  address: string;
  district?: string;
  city?: string;
  phone: string;
  email?: string;
  taxCode?: string;
  logo?: string;
  receiptFooter?: string;
  workingHours?: string;
  /** Auto-print receipt sau thanh toán POS (dùng cho EG-83). */
  autoPrint?: boolean;
  /** Loại vàng kinh doanh (EG-85 §1). */
  goldTypes?: string[];
  /** Múi giờ (EG-85 §1). Default: Asia/Ho_Chi_Minh. */
  timezone?: string;
  /** Đơn vị tiền tệ (EG-85 §1). Default: VND. */
  currency?: string;
}

export interface StoreInventorySettings {
  inventory: {
    valuation_method: StoreValuationMethod;
    lot_tracking_enabled: boolean;
    expiry_tracking_enabled: boolean;
  };
}

export interface UpdateStoreInventorySettingsDto {
  valuation_method?: StoreValuationMethod;
  lot_tracking_enabled?: boolean;
  expiry_tracking_enabled?: boolean;
}

export interface LogoUploadResponse {
  url: string;
}

export interface ThemeTokens {
  colorPrimary: string;
  sidebarBg: string;
  sidebarActiveBg: string;
  sidebarText: string;
}

interface ThemeTokensApi {
  color_primary?: string;
  sidebar_bg?: string;
  sidebar_active_bg?: string;
  sidebar_text?: string;
}

function toThemeTokens(raw: ThemeTokensApi | ThemeTokens | null | undefined): ThemeTokens {
  const src = (raw ?? {}) as ThemeTokensApi & Partial<ThemeTokens>;
  return {
    colorPrimary: src.color_primary ?? src.colorPrimary ?? '#a4193d',
    sidebarBg: src.sidebar_bg ?? src.sidebarBg ?? '#1a1a2e',
    sidebarActiveBg: src.sidebar_active_bg ?? src.sidebarActiveBg ?? '#a4193d',
    sidebarText: src.sidebar_text ?? src.sidebarText ?? '#ffffff',
  };
}

function toThemeTokensApi(body: Partial<ThemeTokens>): ThemeTokensApi {
  const payload: ThemeTokensApi = {};
  if (body.colorPrimary) payload.color_primary = body.colorPrimary;
  if (body.sidebarBg) payload.sidebar_bg = body.sidebarBg;
  if (body.sidebarActiveBg) payload.sidebar_active_bg = body.sidebarActiveBg;
  if (body.sidebarText) payload.sidebar_text = body.sidebarText;
  return payload;
}

export const storeService = {
  getSettings: () => api.get<StoreSettings>('/stores/current').then((r) => r.data),

  updateSettings: (body: Partial<StoreSettings>) =>
    api.put<StoreSettings>('/stores/current', body).then((r) => r.data),

  getStoreSettings: (id: string) =>
    api.get<StoreInventorySettings>(`/stores/${id}/settings`).then((r) => r.data),

  updateStoreSettings: (id: string, body: Partial<UpdateStoreInventorySettingsDto>) =>
    api.patch<StoreInventorySettings>(`/stores/${id}/settings`, body).then((r) => r.data),

  getTheme: () => api.get<ThemeTokensApi>('/store/theme').then((r) => toThemeTokens(r.data)),

  updateTheme: (body: Partial<ThemeTokens>) =>
    api.put<ThemeTokensApi>('/store/theme', toThemeTokensApi(body)).then((r) => toThemeTokens(r.data)),

  uploadLogo: (file: File) => {
    const form = new FormData();
    form.append('file', file);
    return api
      .post<LogoUploadResponse>('/store/logo', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      .then((r) => r.data);
  },

  /** List stores (chi nhánh) of tenant. Dùng cho Select gán nhân viên (EG-85 §2)
   *  + warehouse filter / assignment.
   *  BE trả envelope `{ data: Store[], pagination }` (store.controller.ts).
   *  Map sang shape FE-friendly + `active` boolean (state===ACTIVE=1).
   */
  listStores: async (): Promise<TenantStore[]> => {
    const arr: unknown[] = [];
    let page = 1;
    let totalPages = 1;
    do {
      const res = await api.get<{ data: unknown[]; pagination?: { total_pages?: number } } | unknown[]>(
        '/stores',
        { params: { page, limit: 200, state: 1 } },
      );
      const body = res.data;
      const pageData = Array.isArray(body) ? body : Array.isArray(body?.data) ? body.data : [];
      arr.push(...pageData);
      totalPages = Array.isArray(body) ? 1 : (body?.pagination?.total_pages ?? 1);
      page += 1;
    } while (page <= totalPages);
    return arr.map((raw): TenantStore => {
      const s = raw as Record<string, unknown>;
      return {
        _id: String(s._id ?? s.id ?? ''),
        code: String(s.code ?? ''),
        name: String(s.name ?? ''),
        address: typeof s.address === 'string' ? s.address : '',
        phone: typeof s.phone === 'string' ? s.phone : typeof s.hotline === 'string' ? s.hotline : undefined,
        taxCode: typeof s.tax_code === 'string' ? s.tax_code : undefined,
        managerId:
          typeof s.manager_staff_id === 'string'
            ? s.manager_staff_id
            : s.manager_staff_id != null
              ? String(s.manager_staff_id)
              : undefined,
        parentStoreId:
          typeof s.parent_store_id === 'string'
            ? s.parent_store_id
            : s.parent_store_id != null
              ? String(s.parent_store_id)
              : null,
        active: s.state === 1,
        openingHours: typeof s.working_hours === 'string' ? s.working_hours : undefined,
      };
    });
  },

  getStore: (id: string) =>
    api.get<Record<string, unknown>>(`/stores/${id}`).then((r) => {
      const raw = r.data;
      const s =
        raw && 'data' in raw && raw.data !== null && typeof raw.data === 'object'
          ? (raw.data as Record<string, unknown>)
          : raw;
      return {
        _id: String(s._id ?? s.id ?? ''),
        code: String(s.code ?? ''),
        name: String(s.name ?? ''),
        address: typeof s.address === 'string' ? s.address : '',
        phone: typeof s.phone === 'string' ? s.phone : typeof s.hotline === 'string' ? s.hotline : undefined,
        taxCode: typeof s.tax_code === 'string' ? s.tax_code : undefined,
        managerId: typeof s.manager_staff_id === 'string' ? s.manager_staff_id : s.manager_staff_id != null ? String(s.manager_staff_id) : undefined,
        parentStoreId:
          typeof s.parent_store_id === 'string'
            ? s.parent_store_id
            : s.parent_store_id != null
              ? String(s.parent_store_id)
              : null,
        active: s.state === 1,
        openingHours: typeof s.working_hours === 'string' ? s.working_hours : undefined,
      } as TenantStore;
    }),

  createStore: (body: {
    name: string;
    code?: string;
    address: string;
    phone?: string;
    taxCode?: string;
    managerId?: string;
    parentStoreId?: string | null;
    openingHours?: string;
    active?: boolean;
  }) => {
    let open_time: string | undefined;
    let close_time: string | undefined;
    if (body.openingHours) {
      const parts = body.openingHours.split('-').map(p => p.trim());
      if (parts.length === 2) {
        open_time = parts[0];
        close_time = parts[1];
      }
    }
    const payload = {
      name: body.name,
      code: body.code?.trim() || undefined,
      address: body.address,
      phone: body.phone?.trim() || undefined,
      ...(body.taxCode !== undefined ? { tax_code: body.taxCode.trim() } : {}),
      manager_id: body.managerId,
      ...(body.parentStoreId ? { parent_store_id: body.parentStoreId } : {}),
      open_time,
      close_time,
      state: body.active === false ? StoreState.INACTIVE : StoreState.ACTIVE,
    };
    return api.post<TenantStore>('/stores', payload).then((r) => r.data);
  },

  updateStore: (
    id: string,
    body: Partial<{
      name: string;
      code: string;
      address: string;
      phone: string;
      taxCode: string;
      managerId: string;
      parentStoreId: string | null;
      openingHours: string;
      active: boolean;
    }>,
  ) => {
    let open_time: string | undefined;
    let close_time: string | undefined;
    if (body.openingHours) {
      const parts = body.openingHours.split('-').map(p => p.trim());
      if (parts.length === 2) {
        open_time = parts[0];
        close_time = parts[1];
      }
    }
    const payload = {
      name: body.name,
      code: body.code?.trim() || undefined,
      address: body.address,
      phone: body.phone?.trim() || undefined,
      ...(body.taxCode !== undefined ? { tax_code: body.taxCode.trim() } : {}),
      manager_id: body.managerId,
      ...(body.parentStoreId !== undefined ? { parent_store_id: body.parentStoreId || null } : {}),
      open_time,
      close_time,
      state: body.active !== undefined ? (body.active ? StoreState.ACTIVE : StoreState.INACTIVE) : undefined,
    };
    return api.patch<TenantStore>(`/stores/${id}`, payload).then((r) => r.data);
  },

  deactivateStore: (id: string) =>
    api.patch<TenantStore>(`/stores/${id}/deactivate`).then((r) => r.data),

  activateStore: (id: string) =>
    api.patch<TenantStore>(`/stores/${id}/activate`).then((r) => r.data),

  deleteStore: (id: string) =>
    api.delete<TenantStore>(`/stores/${id}`).then((r) => r.data),

  // EG-390 — multi-store report
  getMultiStoreReport: (params: {
    storeIds?: string[];
    startDate?: string;
    endDate?: string;
  }) =>
    api
      .get<{
        comparison: Array<{
          store_id: string;
          store_name: string;
          revenue: number;
          orders: number;
          new_customers: number;
          delta_vs_prev_period: number;
        }>;
        total_revenue: number;
        total_orders: number;
        total_new_customers: number;
      }>('/reports/multi-store', {
        params: {
          store_ids: params.storeIds,
          start_date: params.startDate,
          end_date: params.endDate,
        },
      })
      .then((r) => ({
        data: (r.data.comparison || []).map((item) => ({
          storeId: item.store_id,
          storeName: item.store_name,
          revenue: item.revenue,
          orders: item.orders,
          newCustomers: item.new_customers,
          deltaVsPrevPeriod: item.delta_vs_prev_period,
        })),
        summary: {
          totalRevenue: r.data.total_revenue || 0,
          totalOrders: r.data.total_orders || 0,
          totalNewCustomers: r.data.total_new_customers || 0,
        },
      })),
};
